import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { createProperty, updateProperty } from '@/lib/api';
import { useUpgradePrompt, detectPlanLimitError } from '@/components/UpgradePromptDialog';
import { toast } from 'sonner';

export const PropertyDialog = ({ open, onClose, editingProperty = null }) => {
  const { showUpgradePrompt } = useUpgradePrompt();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    description: '',
    image_url: '',
    bedrooms: 1,
    bathrooms: 1,
  });

  useEffect(() => {
    if (!open) return;
    if (editingProperty) {
      setFormData({
        name: editingProperty.name || '',
        address: editingProperty.address || '',
        description: editingProperty.description || '',
        image_url: editingProperty.image_url || '',
        bedrooms: editingProperty.bedrooms || 1,
        bathrooms: editingProperty.bathrooms || 1,
      });
    } else {
      setFormData({
        name: '',
        address: '',
        description: '',
        image_url: '',
        bedrooms: 1,
        bathrooms: 1,
      });
    }
  }, [open, editingProperty]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.address) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      if (editingProperty) {
        await updateProperty(editingProperty.id, formData);
        toast.success('Property updated successfully');
      } else {
        await createProperty(formData);
        toast.success('Property created successfully');
      }
      onClose();
    } catch (error) {
      const resource = detectPlanLimitError(error);
      if (resource) {
        showUpgradePrompt({ resource, detail: error.response?.data?.detail });
        onClose();
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save property');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'bedrooms' || name === 'bathrooms' ? parseInt(value) || 0 : value
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="property-dialog">
        <DialogHeader>
          <DialogTitle className="font-manrope">
            {editingProperty ? 'Edit Property' : 'Add New Property'}
          </DialogTitle>
          <DialogDescription className="text-slate-500">Property details — address, bedrooms, bathrooms, image.</DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Property Name *</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Beach House"
              className="bg-slate-50"
              data-testid="property-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Address *</Label>
            <Input
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="123 Ocean Drive, Miami, FL"
              className="bg-slate-50"
              data-testid="property-address-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="bedrooms">Bedrooms</Label>
              <Input
                id="bedrooms"
                name="bedrooms"
                type="number"
                min="0"
                value={formData.bedrooms}
                onChange={handleChange}
                className="bg-slate-50"
                data-testid="property-bedrooms-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bathrooms">Bathrooms</Label>
              <Input
                id="bathrooms"
                name="bathrooms"
                type="number"
                min="0"
                value={formData.bathrooms}
                onChange={handleChange}
                className="bg-slate-50"
                data-testid="property-bathrooms-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="image_url">Image URL (optional)</Label>
            <Input
              id="image_url"
              name="image_url"
              value={formData.image_url}
              onChange={handleChange}
              placeholder="https://..."
              className="bg-slate-50"
              data-testid="property-image-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe your property..."
              className="bg-slate-50"
              rows={3}
              data-testid="property-description-input"
            />
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              data-testid="property-cancel-btn"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="property-submit-btn"
            >
              {loading
                ? (editingProperty ? 'Saving...' : 'Creating...')
                : (editingProperty ? 'Save Changes' : 'Create Property')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PropertyDialog;
